<template>
  <div class="accordion-container">
    <div class="button-row mb-4">
      <v-btn class="action-button discard-button" @click="handleDiscard">Discard</v-btn>
      <v-btn class="action-button" @click="handleSave">Save Your Edits</v-btn>
      <v-btn class="action-button" @click="handleSubmit">Submit Edits to BBAS</v-btn>
      <v-btn class="action-button test-button" @click="handleTest">Test</v-btn>
      <v-btn 
        v-if="isOwner"
        class="action-button" 
        @click="handleDeploy"
      >Deploy</v-btn>
    </div>

    <draggable 
      v-model="items"
      item-key="id"
      handle=".kebab-handle"
      @end="onDragEnd"
      chosen-class="dragging"
      animation="300"
      :disabled="!!editingId"
    >
      <template #item="{ element, index }">
        <div :class="[
          'accordion-item', 
          { 
            'deletion-animation': deletingId === element.id,
            'addition-animation': addingId === element.id 
          }
        ]">
          <v-expansion-panels v-model="expandedPanel[index]">
            <v-expansion-panel>
             <v-expansion-panel-title>
               <div class="d-flex align-center w-100" @click.stop>
                 <v-btn
                   v-if="showTranslateButtons.has(element.id)"
                   class="translation-button"
                   color="#d2b48c"
                   variant="flat"
                   @click.stop="handleTranslate(element.id)" 
                 >
                   Translate
                 </v-btn>
                 <div class="left-section d-flex align-center">
                   <span class="kebab-handle me-3 text-grey" :class="{ 'disabled': editingId === element.id }">⋮⋮</span>
                   <button class="kebab-menu mx-3" @click.stop="showKebabMenu(index)">⋮</button>
                   <div v-if="isKebabOpen(index)" class="kebab-menu-dropdown" ref="kebabMenu">
                     <button @click.stop="addItem(index)">Add</button>
                     <button @click.stop="deleteItem(element.id, index)">Delete</button>
                   </div>
                 </div>
                 
                 <div v-if="editingId === element.id" class="d-flex align-center flex-grow-1">
                   <v-text-field
                     v-model="editingText"
                     density="compact"
                     variant="outlined"
                     hide-details
                     @click.stop
                     @keyup.enter="saveEdit(element.id)"
                     @keyup.esc="cancelEdit"
                     ref="editInput"
                   ></v-text-field>
                   <v-icon 
                     class="ms-2" 
                     color="success" 
                     size="small"
                     @click.stop="saveEdit(element.id)"
                   >
                     mdi-check
                   </v-icon>
                   <v-icon 
                     class="ms-2" 
                     color="error" 
                     size="small"
                     @click.stop="cancelEdit"
                   >
                     mdi-close
                   </v-icon>
                 </div>
                 
                 <div v-else class="d-flex align-center flex-grow-1">
                   <span class="item-title">{{ element.prompt['en-US'] }}</span>
                   <v-icon 
                     class="ms-2 edit-icon" 
                     size="small"
                     @click.stop="startEdit(element.id, element.prompt['en-US'], index)"
                   >
                     mdi-pencil
                   </v-icon>
                 </div>
               </div>
              <div 
                v-if="isOwner" 
                class="target-info"
              >
                <span v-if="element.noBranchTarget">noBranchTarget={{element.noBranchTarget}}</span>
                <span v-if="element.yesBranchTarget">yesBranchTarget={{element.yesBranchTarget}}</span>
                <span v-if="element.skipToTarget">skipToTarget={{element.skipToTarget}}</span>
                <span v-if="element.targetId">targetId={{element.targetId}}</span>
              </div>
             </v-expansion-panel-title>
             <v-expansion-panel-text>
               <div class="translation-container">
                 <div class="translation-item">
                   {{ element.prompt['es-MX'] }}
                 </div>
                 <div class="translation-item">{{ element.prompt['ru-RU'] }}</div>
               </div>
               <div v-if="isOwner" class="properties-button-container">
                 <v-btn
                   color="#8b4513"
                   class="properties-button"
                   elevation="2"
                   icon
                   size="32"
                   @click.stop="currentEditItem = element; showPropertiesDialog = true"
                 />
               </div>
             </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </template>
    </draggable>

    <JailCallMenuEditorPropertiesDialog
      v-model="showPropertiesDialog"
      :item="currentEditItem || {}"
      @save="handlePropertiesSave"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import draggable from 'vuedraggable';
import axios from 'axios';
import JailCallMenuEditorPropertiesDialog from '@/components/Settings-JailCallMenuEditor-PropertiesDialog.vue';
import { prettyPhone } from '../utils/helpers';

export default {
  name: 'JailCallMenuEditor',
  components: {
    draggable,
    JailCallMenuEditorPropertiesDialog
  },
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  emits: ['update:company', 'show-dialog'],
  setup(props, { emit }) {
    const store = useStore();
    const items = ref(
      props.company?.editIntakeMenu || 
      props.company?.intakeMenu || 
      []
    );
    const expandedPanel = ref(Array(items.value.length).fill(null));
    const editingId = ref(null);
    const editingText = ref('');
    const editInput = ref(null);
    const openKebabIndex = ref(null);
    const deletingId = ref(null);
    const addingId = ref(null);
    const showTranslateButtons = ref(new Set());
    const showPropertiesDialog = ref(false);
    const currentEditItem = ref(null);
    const isOwner = computed(() => store.state.user?.role === 'Owner');

    const showKebabMenu = (index) => {
      openKebabIndex.value = openKebabIndex.value === index ? null : index;
    };

    const isKebabOpen = (index) => openKebabIndex.value === index;

    const generateId = () => Math.max(...items.value.map(item => item.id), 0) + 1;

    const addItem = async (index) => {
      const newItem = {
        id: generateId(),
        prompt: {
          'en-US': 'New Item',
          'es-MX': 'Artículo Nuevo',
          'ru-RU': 'Новый товар'
        }
      };
      
      items.value.splice(index + 1, 0, newItem);
      expandedPanel.value.splice(index + 1, 0, null);
      addingId.value = newItem.id;
      openKebabIndex.value = null;
      // showTranslateButtons.value.add(newItem.id);

      setTimeout(() => {
        addingId.value = null;
      }, 300);
    };

    const startEdit = async (id, text, index) => {
      expandedPanel.value[index] = 0;
      editingId.value = id;
      editingText.value = text;
      await nextTick();
      editInput.value?.focus();
    };

    const saveEdit = (id) => {
      const item = items.value.find(item => item.id === id);
      if (item) {
        item.prompt['en-US'] = editingText.value;
        item.prompt['es-MX'] = '';
        item.prompt['ru-RU'] = '';
        showTranslateButtons.value.add(id);
      }
      editingId.value = null;
      editingText.value = '';
    };

    const cancelEdit = () => {
      editingId.value = null;
      editingText.value = '';
    };

    const deleteItem = async (id, index) => {
      deletingId.value = id;
      openKebabIndex.value = null;
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      items.value = items.value.filter(item => item.id !== id);
      expandedPanel.value.splice(index, 1);
      deletingId.value = null;
      showTranslateButtons.value.delete(id);
    };

    const handleClickOutside = (event) => {
      if (!event.target.closest('.kebab-menu-dropdown') && 
          !event.target.closest('.kebab-menu')) {
        openKebabIndex.value = null;
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        openKebabIndex.value = null;
      }
    };

    const onDragEnd = () => {
      // Additional drag end handling can be added here if needed
    };

    const handleTranslate = async (id) => {
      try {
        const item = items.value.find(item => item.id === id);
        if (!item) {
          throw new Error('Item not found');
        }

        const response = await axios.post('/portaldb/db/translateMenuItem', {
          English: item.prompt['en-US']
        });

        if (response.data) {
          item.prompt['es-MX'] = response.data['es-MX'];
          item.prompt['ru-RU'] = response.data['ru-RU'];
          showTranslateButtons.value.delete(id);
        } else {
          emit('show-dialog', 'Translation failed. Please try again.');
        }
      } catch (error) {
        console.error('Translation error:', error);
        emit('show-dialog', 'An error occurred during translation. Please try again.');
      }
    };

    const handleDiscard = async () => {
      try {
        const response = await axios.get(`/portaldb/db/companies/${props.company.companyNumber}`);
        
        if (response.data) {
          emit('update:company', response.data);
          
          items.value = JSON.parse(JSON.stringify(
            response.data?.editIntakeMenu || 
            response.data?.intakeMenu || 
            []
          ));

          showTranslateButtons.value.clear();

          items.value.forEach(item => {
            const hasSpanish = item.prompt['es-MX']?.trim();
            const hasRussian = item.prompt['ru-RU']?.trim();
            
            if (!hasSpanish || !hasRussian) {
              showTranslateButtons.value.add(item.id);
            }
          });
          
          emit('show-dialog', 'Your changes have been discarded.');
        } else {
          emit('show-dialog', 'There was an error discarding your changes. Please try again.');
        }
      } catch (error) {
        console.error('Error discarding changes:', error);
        emit('show-dialog', 'There was an error discarding your changes. Please try again.');
      }
    };

    const handleDeploy = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/deployMenu/${props.company.companyNumber}`, 
          { 
            intakeMenu: items.value,
            editIntakeMenu: null 
          }
        );

        if (response.data.success) {
          emit('update:company', { 
            intakeMenu: items.value,
            editIntakeMenu: null
          });
          emit('show-dialog', 'Your menu has been successfully deployed.');
        } else {
          emit('show-dialog', 'There was an error deploying your menu. Please try again.');
        }
      } catch (error) {
        console.error('Error deploying menu:', error);
        emit('show-dialog', 'There was an error deploying your menu. Please try again.');
      }
    };

    const handleSave = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/saveMenuEdits/${props.company.companyNumber}`, 
          { editIntakeMenu: items.value }
        );

        if (response.data.success) {
          emit('update:company', { editIntakeMenu: items.value });
          emit('show-dialog', 'Your edits have been saved. When you are ready to deploy your changes click SUBMIT. <br/><br/> <b>We will not know you have changes until you click SUBMIT.</b>');
        } else {
          emit('show-dialog', 'There was an error saving your edits. Please try again.');
        }
      } catch (error) {
        console.error('Error saving menu edits:', error);
        emit('show-dialog', 'There was an error saving your edits. Please try again.');
      }
    };

    const sendEmailNotification = async () => {
      try {
        await axios.post('/portaldb/db/simpleSendEmail', {
          recipientEmail: 'support@BailBondAgentServices.com',
          subject: `Menu Edit for ${props.company.name} ${props.company.companyNumber}`,
          textToSend: 'Do it.'
        });
      } catch (error) {
        console.error('Error sending email notification:', error);
      }
    };

    const handleSubmit = async () => {
      try {
        await sendEmailNotification();
        emit('show-dialog', 'Your changes have been submitted for implementation. Allow about four hours for these changes to be deployed.');
      } catch (error) {
        console.error('Error in submit process:', error);
        emit('show-dialog', 'Your changes have been submitted for implementation. Allow about four hours for these changes to be deployed.');
      }
    };

    const handleTest = async () => {
      emit('show-dialog', `You can always test your currently deployed menu by calling directly into the menu system at: ${prettyPhone(props.company.menuDirectPhone)}`);
    };

    const handlePropertiesSave = (updates) => {
      const item = items.value.find(i => i.id === currentEditItem.value.id);
      if (item) {
        Object.keys(item).forEach(key => {
          if (key !== 'id' && key !== 'prompt' && !(key in updates)) {
            delete item[key];
          }
        });
        Object.assign(item, updates);
      }
    };

    onMounted(() => {
      window.addEventListener('click', handleClickOutside);
      window.addEventListener('keydown', handleEscKey);
      
      items.value.forEach(item => {
        const hasSpanish = item.prompt['es-MX']?.trim();
        const hasRussian = item.prompt['ru-RU']?.trim();
        if (!hasSpanish || !hasRussian) {
          showTranslateButtons.value.add(item.id);
        }
      });
    });

    onUnmounted(() => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('keydown', handleEscKey);
    });

    return {
      items,
      expandedPanel,
      editingId,
      editingText,
      editInput,
      deletingId,
      addingId,
      showTranslateButtons,
      showPropertiesDialog,
      currentEditItem,
      isOwner,
      showKebabMenu,
      isKebabOpen,
      addItem,
      deleteItem,
      startEdit,
      saveEdit,
      cancelEdit,
      handleTranslate,
      handleSave,
      handleDeploy,
      handleSubmit,
      handleTest,
      handleDiscard,
      handlePropertiesSave,
      onDragEnd
    };
  }
};
</script>

<style scoped>

.target-info {
  position: absolute;
  top: 4px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 10px;
  color: #8b4513;
  opacity: 0.7;
}

.target-info span {
  margin-bottom: 2px;
}

:deep(.v-expansion-panel-title) {
  pointer-events: none;
}

:deep(.v-expansion-panel-title__icon),
:deep(.kebab-handle),
:deep(.kebab-menu),
:deep(.kebab-menu-dropdown),
:deep(.edit-icon),
:deep(.v-text-field),
:deep(.translation-button),
:deep(.v-icon) {
  pointer-events: auto;
}

.accordion-container {
  background-color: #f5f5dc;
  padding: 20px;
  border-radius: 8px;
}

.button-row {
  display: flex;
  gap: 12px;
}

.action-button {
  background-color: #d2b48c !important;
  color: white !important;
  transition: background-color 0.3s ease !important;
}

.action-button:hover {
  background-color: #635347 !important;
}

.kebab-handle {
  cursor: move;
  font-size: 16px;
  line-height: 1;
  opacity: 0.7;
  padding: 8px 4px;
}

.kebab-handle.disabled {
  cursor: default;
  opacity: 0.3;
}

.edit-icon {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.edit-icon:hover {
  opacity: 1;
}

.kebab-menu {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #8b4513;
  padding: 4px 8px;
  margin: 0 12px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  outline: none;
}

.kebab-menu:hover {
  opacity: 1;
}

.kebab-menu-dropdown {
  position: absolute;
  background-color: white;
  border: 1px solid #d2b48c;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 5px 0;
  z-index: 1000;
  left: 45px;
  top: 0;
  min-width: 120px;
  transform: translateY(-25%);
}


.kebab-menu-dropdown button {
  font-size: 14px;
  display: block;
  width: 100%;
  padding: 8px 20px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.kebab-menu-dropdown button:hover {
  background-color: #f5f5dc;
}

.accordion-item {
  transition: all 0.3s ease-out;
  transform-origin: top;
  opacity: 1;
  transform: scale(1);
}

.deletion-animation {
  opacity: 0;
  transform: scale(0.8);
}

.addition-animation {
  animation: addItem 0.3s ease-out;
}

@keyframes addItem {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.dragging {
  opacity: 0.4;
}

:deep(.v-text-field input) {
  font-size: 15px !important;
}

.item-title {
  line-height: 20px;
  font-size: 15px;
}

.test-button {
  display: none;
}

.discard-button {
  display:none;
}

:deep(.v-expansion-panel-title) {
  min-height: 64px !important;
  padding: 16px !important;
}

:deep(.v-expansion-panel-title__overlay) {
  height: 64px !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  background-color: #d2b48c !important;
  border: 1px solid #d2b48c !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #ffffff !important;
}

:deep(.draggable .v-expansion-panels .v-expansion-panel .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon), 
:deep(.accordion-container .v-expansion-panels .v-expansion-panel .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #8b4513 !important;
}

:deep(.v-expansion-panel-text) {
  background-color: #f5f5dc !important;
  border: 1px solid #d2b48c !important;
  border-top: none !important;
}

:deep(.accordion-container .v-expansion-panels .v-expansion-panel > .v-expansion-panel-title),
:deep(.accordion-container .v-expansion-panels .v-expansion-panel > .v-expansion-panel-text) {
  background-color: #ffffff !important;
  color: #424242 !important;
}

:deep(.accordion-container .v-expansion-panels .v-expansion-panel > .v-expansion-panel-title:hover) {
  background-color: #f5f5f5 !important;
}

.translation-container {
  padding-left: 0;
  width: 100%;
  position: relative;
}

.translation-button {
  position: absolute !important;
  left: 4px !important;
  top: 4px !important;
  transform: none !important;
  height: 20px !important;
  min-width: 80px !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
  padding: 4px !important;
  z-index: 1;
}

.translation-item {
  width: 100%;
  position: relative;
}

:deep(#menu-edit-panel) {
  margin-bottom: 22px;
}

.properties-button-container {
  position: relative;
  height: 0;
}

.properties-button {
  position: absolute !important;
  bottom: -10px !important;   /* Reduced from original value */
  right: -2px !important;    /* Reduced from original value */
  height: 16px !important;  /* Reduced from 32px */
  width: 16px !important;   /* Added to maintain circle shape */
  min-width: 16px !important; /* Added to ensure button stays small */
}

</style>

